import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Clock, BookOpen, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { blogPosts } from '../data/blogPosts';
const categories = ['All', 'Investment Tips', 'Home Buying', 'Market Insights', 'Real Estate Trends'];
export function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const featured = blogPosts.find(p => p.featured)!;
  const others = blogPosts.filter(p => !p.featured);
  const filtered = others.filter(p => activeCategory === 'All' || p.category === activeCategory);
  return <div className="pt-28 pb-24" data-id="element-759">
      <PageHeader eyebrow="Media & Insights" title="Zylus Media" subtitle="Expert insights, market trends, and investment intelligence — published by the editorial team at Zylus Media." breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Blog'
    }]} data-id="element-760" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-12 mt-12" data-id="element-761">
        {/* Featured Post */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-ink/5 flex flex-col lg:flex-row mb-20 group" data-id="element-762">
          <Link to={`/blog/${featured.slug}`} className="lg:w-3/5 h-72 lg:h-auto relative overflow-hidden" data-id="element-763">
            <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-764" />
            <div className="absolute top-5 left-5 bg-gold text-ink px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase flex items-center gap-1.5" data-id="element-765">
              <Sparkles className="w-3 h-3" data-id="element-766" /> Featured
            </div>
          </Link>
          <div className="lg:w-2/5 p-8 lg:p-12 flex flex-col justify-center" data-id="element-767">
            <div className="inline-flex items-center gap-2 bg-gold/10 text-ink px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase mb-6 self-start" data-id="element-768">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" data-id="element-769" />
              {featured.category}
            </div>
            <Link to={`/blog/${featured.slug}`} data-id="element-770">
              <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-4 leading-tight group-hover:text-gold transition-colors" data-id="element-771">
                {featured.title}
              </h2>
            </Link>
            <p className="text-ink/60 mb-6 leading-relaxed" data-id="element-772">
              {featured.excerpt}
            </p>

            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-ink/10" data-id="element-773">
              <div className="w-10 h-10 rounded-full bg-ink flex items-center justify-center text-white font-serif font-semibold" data-id="element-774">
                Z
              </div>
              <div data-id="element-775">
                <div className="text-sm font-semibold text-ink" data-id="element-776">
                  {featured.publisher}
                </div>
                <div className="text-xs text-ink/50" data-id="element-777">
                  By {featured.author} · {featured.date}
                </div>
              </div>
            </div>

            <Link to={`/blog/${featured.slug}`} className="inline-flex items-center gap-2 bg-ink hover:bg-gold hover:text-ink text-white text-sm font-semibold px-6 py-3.5 rounded-xl transition-colors w-fit group/btn" data-id="element-778">
              <BookOpen className="w-4 h-4" data-id="element-779" />
              Read Full Article
              <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-780" />
            </Link>
          </div>
        </motion.div>

        {/* Categories */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10" data-id="element-781">
          <div data-id="element-782">
            <h3 className="text-3xl font-serif text-ink mb-2" data-id="element-783">
              Latest From Our Blog
            </h3>
            <p className="text-sm text-ink/50" data-id="element-784">
              Published by Zylus Media · Updated weekly
            </p>
          </div>
          <div className="hidden md:flex flex-wrap gap-2" data-id="element-785">
            {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${activeCategory === cat ? 'bg-ink text-white shadow-md' : 'bg-white text-ink/70 border border-ink/10 hover:border-ink/30'}`} data-id="element-786">
                {cat}
              </button>)}
          </div>
        </div>

        {/* Mobile Categories */}
        <div className="md:hidden flex overflow-x-auto gap-2 pb-4 mb-6 hide-scrollbar" data-id="element-787">
          {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-all ${activeCategory === cat ? 'bg-ink text-white shadow-md' : 'bg-white text-ink/70 border border-ink/10 hover:border-ink/30'}`} data-id="element-788">
              {cat}
            </button>)}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-789">
          <AnimatePresence mode="popLayout" data-id="element-790">
            {filtered.map((post, i) => <motion.article key={post.id} layout initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            delay: i % 3 * 0.08,
            duration: 0.5
          }} whileHover={{
            y: -6
          }} className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-ink/5 hover:shadow-xl hover:border-gold/30 transition-all duration-500 flex flex-col" data-id="element-791">
                <Link to={`/blog/${post.slug}`} className="relative h-56 overflow-hidden block" data-id="element-792">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-793" />
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-ink px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase" data-id="element-794">
                    {post.category}
                  </div>
                </Link>

                <div className="p-6 flex flex-col flex-grow" data-id="element-795">
                  {/* Publisher line */}
                  <div className="flex items-center gap-2 mb-3 text-[10px] font-bold uppercase tracking-widest text-gold-dark" data-id="element-796">
                    <span className="w-5 h-5 rounded-full bg-ink flex items-center justify-center text-white text-[8px] font-serif" data-id="element-797">
                      Z
                    </span>
                    {post.publisher}
                  </div>

                  <Link to={`/blog/${post.slug}`} data-id="element-798">
                    <h4 className="text-xl font-serif text-ink mb-3 group-hover:text-gold transition-colors line-clamp-2" data-id="element-799">
                      {post.title}
                    </h4>
                  </Link>
                  <p className="text-ink/60 text-sm mb-5 flex-grow line-clamp-3" data-id="element-800">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between text-xs text-ink/50 pt-4 border-t border-ink/5 mb-4" data-id="element-801">
                    <span data-id="element-802">{post.date}</span>
                    <span className="flex items-center gap-1" data-id="element-803">
                      <Clock className="w-3 h-3" data-id="element-804" /> {post.readTime}
                    </span>
                  </div>

                  <Link to={`/blog/${post.slug}`} className="inline-flex items-center justify-center gap-2 bg-bone hover:bg-gold text-ink text-xs font-bold uppercase tracking-widest py-3 rounded-xl transition-colors group/btn" data-id="element-805">
                    Read Article
                    <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" data-id="element-806" />
                  </Link>
                </div>
              </motion.article>)}
          </AnimatePresence>
        </div>

        {/* Newsletter strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-24 bg-ink rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" data-id="element-807">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gold/15 blur-3xl pointer-events-none" data-id="element-808" />
          <div className="relative" data-id="element-809">
            <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-gold mb-4" data-id="element-810">
              Subscribe to Zylus Media
            </div>
            <h3 className="text-3xl md:text-4xl font-serif text-white mb-4" data-id="element-811">
              Property intelligence, in your inbox.
            </h3>
            <p className="text-white/60 max-w-xl mx-auto mb-8 text-sm" data-id="element-812">
              Weekly market briefings, off-market opportunities, and editorial
              deep-dives — written by Zylus Media.
            </p>
            <form onSubmit={e => e.preventDefault()} className="max-w-md mx-auto flex flex-col sm:flex-row gap-3" data-id="element-813">
              <input type="email" placeholder="your@email.com" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-gold transition-colors" data-id="element-814" />
              <button className="bg-gold hover:bg-gold-dark text-ink font-bold tracking-widest uppercase text-xs px-6 py-3.5 rounded-xl transition-colors whitespace-nowrap" data-id="element-815">
                Subscribe
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>;
}